import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';

import { NotificationChannelSettings } from '@apps/web/components/settings/notification_channel_settings';
import {
    type NotificationPreferencesUpdateBody,
    patchNotificationPreferences,
} from '@apps/web/components/settings/notification_preference_updates';
import {
    SettingsRow,
    SettingsSection,
} from '@apps/web/components/settings/settings_primitives';
import { useEmailUnsubscribeDeepLink } from '@apps/web/components/settings/use_email_unsubscribe_deep_link';
import { useNotificationSettingsData } from '@apps/web/components/settings/use_notification_settings_data';
import { Switch } from '@apps/web/components/ui/switch';
import {
    SettingsPageSections,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { toast } from '@apps/web/lib/app_notifications';
import { EVENTS } from '@apps/web/lib/events';
import {
    isNotificationChannelSlug,
    notificationChannelPageForSlug,
} from '@apps/web/lib/notification_channel_pages';

export const Route = createFileRoute('/_app/settings/notifications/$channel')({
    beforeLoad: ({ params }) => {
        if (!isNotificationChannelSlug(params.channel)) {
            throw redirect({ to: '/settings/notifications' });
        }
    },
    component: NotificationChannelRoutePage,
});

function NotificationChannelRoutePage() {
    const { channel: slug } = Route.useParams();
    if (!isNotificationChannelSlug(slug)) {
        return null;
    }
    return <NotificationChannelRoute slug={slug} />;
}

function NotificationChannelRoute({
    slug,
}: {
    readonly slug: 'email' | 'web' | 'text';
}) {
    const { organizationId } = useSettingsGates();
    const page = notificationChannelPageForSlug(slug);
    const posthog = usePostHog();
    const {
        isLoading,
        isSmsNumberConfigured,
        mutate,
        notificationPreferences,
        schedules,
        sections,
    } = useNotificationSettingsData(organizationId);

    useEmailUnsubscribeDeepLink({
        enabled: page.channel === 'email',
        organizationId,
        isLoading,
        preferences: notificationPreferences,
        onMutate: mutate,
    });

    const patchWebSetting = useCallback(
        async (body: NotificationPreferencesUpdateBody, setting: string) => {
            if (!organizationId) {
                toast.error('Select an organization first', {
                    persistNotification: true,
                    notificationType: 'settings_update',
                });
                return;
            }
            if (setting === 'browser' && body['browser_enabled'] === true) {
                if (typeof Notification === 'undefined') {
                    toast.error(
                        'Browser notifications are not supported in this browser',
                        {
                            persistNotification: true,
                            notificationType: 'settings_update',
                        },
                    );
                    return;
                }
                const permission =
                    Notification.permission === 'granted'
                        ? 'granted'
                        : await Notification.requestPermission();
                if (permission !== 'granted') {
                    toast.error(
                        permission === 'denied'
                            ? 'Browser notifications are blocked for Phoebe in this browser'
                            : 'Browser notification permission was not granted',
                        {
                            persistNotification: true,
                            notificationType: 'settings_update',
                        },
                    );
                    return;
                }
            }
            try {
                await patchNotificationPreferences(organizationId, body);
                await mutate();
                posthog?.capture(EVENTS.NOTIFICATION_PREFERENCE_TOGGLED, {
                    setting,
                    enabled: Object.values(body)[0] === true,
                });
            } catch {
                toast.error('Failed to update preference', {
                    persistNotification: true,
                    notificationType: 'settings_update',
                });
            }
        },
        [mutate, organizationId, posthog],
    );

    const autoDisabledTypes =
        page.channel === 'email'
            ? (notificationPreferences?.email_auto_disabled_types ?? [])
            : page.channel === 'inapp'
              ? (notificationPreferences?.inapp_auto_disabled_types ?? [])
              : [];

    return (
        <SettingsPageShell
            title={page.label}
            eyebrow={
                <Link
                    to="/settings/notifications"
                    activeOptions={{ exact: true }}
                    className="text-muted-foreground hover:text-foreground inline-flex text-sm"
                >
                    ← Notifications
                </Link>
            }
        >
            <SettingsPageSections>
                <NotificationChannelSettings
                    organizationId={organizationId}
                    page={page}
                    isLoading={isLoading}
                    isSmsNumberConfigured={isSmsNumberConfigured}
                    preferences={notificationPreferences}
                    schedules={schedules}
                    sections={sections}
                    autoDisabledTypes={autoDisabledTypes}
                    onMutate={mutate}
                    extraSections={
                        page.channel === 'inapp' ? (
                            <SettingsSection
                                title="In-app alerts"
                                description="Control local browser behavior for in-app notifications."
                            >
                                <SettingsRow
                                    htmlFor="notification-browser-enabled"
                                    title="Browser notifications"
                                    description="Show native browser notifications when Phoebe is not the active tab"
                                    control={
                                        <Switch
                                            id="notification-browser-enabled"
                                            checked={
                                                notificationPreferences?.browser_enabled ??
                                                false
                                            }
                                            onCheckedChange={(checked) =>
                                                void patchWebSetting(
                                                    {
                                                        browser_enabled:
                                                            checked,
                                                    },
                                                    'browser',
                                                )
                                            }
                                        />
                                    }
                                />
                                <SettingsRow
                                    htmlFor="notification-sound-enabled"
                                    title="Notification sound"
                                    description="Play a sound when in-app notifications arrive"
                                    control={
                                        <Switch
                                            id="notification-sound-enabled"
                                            checked={
                                                notificationPreferences?.sound_enabled ??
                                                true
                                            }
                                            onCheckedChange={(checked) =>
                                                void patchWebSetting(
                                                    {
                                                        sound_enabled: checked,
                                                    },
                                                    'sound',
                                                )
                                            }
                                        />
                                    }
                                />
                                <SettingsRow
                                    htmlFor="notification-toast-global-enabled"
                                    title="Pop-up notifications"
                                    description="Show pop-ups in the corner of the screen. Turning this off keeps everything in the bell"
                                    control={
                                        <Switch
                                            id="notification-toast-global-enabled"
                                            checked={
                                                notificationPreferences?.toast_global_enabled ??
                                                true
                                            }
                                            onCheckedChange={(checked) =>
                                                void patchWebSetting(
                                                    {
                                                        toast_global_enabled:
                                                            checked,
                                                    },
                                                    'toast_global',
                                                )
                                            }
                                        />
                                    }
                                />
                            </SettingsSection>
                        ) : null
                    }
                />
            </SettingsPageSections>
        </SettingsPageShell>
    );
}
